import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import { HighlightOffRounded } from '@material-ui/icons';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import CircularLoader from '../loader/CircularLoader';
import { insertProjectlessUser } from '../../models/api/users';

type AddCompanyUserDialogProps = {
  open: boolean;
  handleClose: () => void;
  companyId: string;
  companyName: string;
  onUserAdded: () => void;
};

const useStyles = makeStyles({
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    height: '80px',
    padding: '8px 16px 8px 16px',
    borderBottom: '1px solid #EDECEC',
    minWidth: 500,
  },
  title: {
    marginTop: '8px',
    marginLeft: '6px',
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '26px',
    lineHeight: '30px',
    textAlign: 'center',
    color: '#0947B9',
    textTransform: 'uppercase',
    justifySelf: 'center',
  },
  rootIconButton: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 8,
  },
  textField: {
    maxWidth: 350,
  },
  submit: {
    marginTop: 16,
    marginBottom: 24,
    maxWidth: 240,
  },
});

export default function AddCompanyUserDialog(props: AddCompanyUserDialogProps) {
  const classes = useStyles();
  const { open, handleClose, companyId, companyName, onUserAdded } = props;

  const [inputName, setInputName] = useState('');
  const [inputEmail, setInputEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setInputName('');
      setInputEmail('');
    }
  }, [open]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await insertProjectlessUser({ name: inputName.trim(), email: inputEmail.trim(), companyId });
      onUserAdded();
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle className={classes.titleContainer}>
        <Typography className={classes.title} align="center">
          Add Company User
        </Typography>
        <IconButton
          id="close-button"
          style={{ right: '20px', top: '16px', position: 'absolute' }}
          onClick={handleClose}
          classes={{
            root: classes.rootIconButton,
          }}
        >
          <HighlightOffRounded />
        </IconButton>
      </DialogTitle>
      <div style={{ height: '17%' }} />
      <DialogContent>
        <form className={classes.form} noValidate>
          <TextField
            variant="outlined"
            margin="normal"
            fullWidth
            required
            id="name"
            label="Name"
            name="name"
            autoFocus
            className={classes.textField}
            value={inputName}
            onChange={(e) => setInputName(e.target.value)}
          />
          <TextField
            variant="outlined"
            margin="normal"
            fullWidth
            required
            id="email"
            label="Email"
            name="email"
            className={classes.textField}
            value={inputEmail}
            onChange={(e) => setInputEmail(e.target.value)}
          />
          <TextField
            variant="outlined"
            margin="normal"
            fullWidth
            disabled
            id="company"
            label="Company"
            name="company"
            className={classes.textField}
            value={companyName}
          />
          {isSubmitting ? (
            <CircularLoader size={30} style={{ marginTop: 16, marginBottom: 24 }} />
          ) : (
            <Button
              fullWidth
              id="add-company-user-button"
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={!inputName.trim() || !inputEmail.trim()}
              onClick={handleSubmit}
            >
              Add
            </Button>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
